/**
 * Enterprise-grade Drag-and-Drop Crew Scheduling Component
 * Implements visual fatigue risk management with real-time predictions
 */
import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  User, Plane, Clock, AlertTriangle, GripVertical,
  Calendar, ChevronLeft, ChevronRight, Sun, Moon,
  Sunrise, Sunset, Battery, BatteryLow, BatteryWarning,
  Zap, MapPin, ArrowRight, Check, X, Plus, Trash2,
  RefreshCw, Download, Eye, EyeOff, Filter, Search
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Risk level configuration
const riskConfig = {
  low: { 
    color: 'bg-emerald-500', 
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20', 
    border: 'border-emerald-500',
    text: 'text-emerald-700 dark:text-emerald-300',
    label: 'Low Risk'
  },
  moderate: { 
    color: 'bg-amber-500', 
    bgLight: 'bg-amber-50 dark:bg-amber-900/20', 
    border: 'border-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
    label: 'Moderate'
  },
  high: { 
    color: 'bg-orange-500', 
    bgLight: 'bg-orange-50 dark:bg-orange-900/20', 
    border: 'border-orange-500',
    text: 'text-orange-700 dark:text-orange-300',
    label: 'High Risk'
  },
  critical: { 
    color: 'bg-red-500', 
    bgLight: 'bg-red-50 dark:bg-red-900/20', 
    border: 'border-red-500',
    text: 'text-red-700 dark:text-red-300',
    label: 'Critical'
  },
  unacceptable: { 
    color: 'bg-rose-700', 
    bgLight: 'bg-rose-50 dark:bg-rose-900/20', 
    border: 'border-rose-700',
    text: 'text-rose-700 dark:text-rose-300',
    label: 'Unacceptable'
  },
};

// Duty type icons
const dutyTypeConfig = {
  flight_duty: { icon: Plane, label: 'Flight', color: 'text-blue-600' },
  positioning: { icon: MapPin, label: 'Positioning', color: 'text-purple-600' },
  standby_airport: { icon: Clock, label: 'APT Standby', color: 'text-amber-600' },
  standby_home: { icon: Moon, label: 'Home Standby', color: 'text-slate-600' },
  reserve: { icon: Battery, label: 'Reserve', color: 'text-cyan-600' },
  training: { icon: Calendar, label: 'Training', color: 'text-indigo-600' },
  simulator: { icon: Zap, label: 'Simulator', color: 'text-violet-600' },
  rest: { icon: Moon, label: 'Rest', color: 'text-emerald-600' },
  off_duty: { icon: Sun, label: 'Off', color: 'text-gray-400' },
};

// Get time period of day
const getTimePeriod = (hour) => {
  if (hour >= 6 && hour < 12) return { icon: Sunrise, label: 'Morning', color: 'text-amber-500' };
  if (hour >= 12 && hour < 18) return { icon: Sun, label: 'Afternoon', color: 'text-yellow-500' };
  if (hour >= 18 && hour < 22) return { icon: Sunset, label: 'Evening', color: 'text-orange-500' };
  return { icon: Moon, label: 'Night', color: 'text-indigo-500' };
};

// ==================== DRAGGABLE CREW CARD ====================
function DraggableCrewCard({ crew, isActive, fatigueScore }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useDraggable({
    id: `crew-${crew.id}`,
    data: { type: 'crew', crew },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const riskLevel = fatigueScore >= 65 ? 'critical' : 
                    fatigueScore >= 50 ? 'high' : 
                    fatigueScore >= 35 ? 'moderate' : 'low';
  const risk = riskConfig[riskLevel];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "p-3 rounded-lg border-2 cursor-grab active:cursor-grabbing transition-all",
        "hover:shadow-md hover:scale-[1.02]",
        isDragging ? "shadow-xl ring-2 ring-indigo-500 z-50" : "",
        isActive ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
      )}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center gap-3">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm",
          risk.color
        )}>
          {fatigueScore || '—'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate text-slate-900 dark:text-white">
            {crew.name}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {crew.role?.replace('_', ' ')} • {crew.employee_id}
          </p>
        </div>
        <GripVertical className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <Badge className={cn("text-xs", risk.bgLight, risk.text)}>
          {risk.label}
        </Badge>
        <span className="text-xs text-slate-500">{crew.home_base}</span>
      </div>
    </div>
  );
}

// ==================== DROPPABLE TIME SLOT ====================
function DroppableTimeSlot({ date, hour, assignments, onRemove, conflicts }) {
  const slotId = `slot-${date}-${hour}`;
  const { isOver, setNodeRef } = useDroppable({
    id: slotId,
    data: { type: 'slot', date, hour },
  });

  const timePeriod = getTimePeriod(hour);
  const TimePeriodIcon = timePeriod.icon;
  const hasConflict = conflicts?.some(c => c.date === date && c.hour === hour);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-[80px] border rounded-lg p-2 transition-all",
        isOver 
          ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 ring-2 ring-indigo-300" 
          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50",
        hasConflict && "border-red-500 bg-red-50 dark:bg-red-900/20"
      )}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1">
          <TimePeriodIcon className={cn("h-3 w-3", timePeriod.color)} />
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
            {hour.toString().padStart(2, '0')}:00
          </span>
        </div>
        {hasConflict && (
          <AlertTriangle className="h-3 w-3 text-red-500 animate-pulse" />
        )}
      </div>
      
      <div className="space-y-1">
        {assignments.map((assignment) => {
          const dutyConfig = dutyTypeConfig[assignment.duty_type] || dutyTypeConfig.flight_duty;
          const DutyIcon = dutyConfig.icon;
          const riskLevel = assignment.predicted_risk_level || 'low';
          const risk = riskConfig[riskLevel];
          
          return (
            <div
              key={assignment.id}
              className={cn(
                "flex items-center gap-1 p-1.5 rounded text-xs group",
                risk.bgLight, "border-l-2", risk.border
              )}
            >
              <DutyIcon className={cn("h-3 w-3 flex-shrink-0", dutyConfig.color)} />
              <span className="flex-1 truncate font-medium text-slate-700 dark:text-slate-200">
                {assignment.crew_member_name || 'Crew'}
              </span>
              <span className={cn("font-bold", risk.text)}>
                {assignment.predicted_fatigue_score || '—'}
              </span>
              <button
                onClick={() => onRemove(assignment.id)}
                className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-opacity"
              >
                <X className="h-3 w-3 text-red-500" />
              </button>
            </div>
          );
        })}
      </div>
      
      {assignments.length === 0 && (
        <div className="flex items-center justify-center h-10 text-xs text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-600 rounded">
          <Plus className="h-3 w-3 mr-1" />
          Drop crew here
        </div>
      )}
    </div>
  );
}

// ==================== CREW CARD OVERLAY (while dragging) ====================
function CrewCardOverlay({ crew }) {
  return (
    <div className="p-3 rounded-lg border-2 border-indigo-500 bg-white dark:bg-slate-800 shadow-2xl cursor-grabbing">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-sm">
          {crew.current_fatigue_score || '—'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{crew.name}</p>
          <p className="text-xs text-slate-500 truncate">{crew.role?.replace('_', ' ')}</p>
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function DragDropScheduler({
  roster,
  entries,
  crew,
  heatmap,
  conflicts,
  onAssign,
  onRemove,
  onAnalyze,
  loading,
}) {
  const [activeItem, setActiveItem] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Generate week dates based on roster period
  const weekDates = useMemo(() => {
    if (!roster) return [];
    
    const startDate = new Date(roster.start_date);
    startDate.setDate(startDate.getDate() + weekOffset * 7);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  }, [roster, weekOffset]);

  // Time slots (focusing on operational hours)
  const timeSlots = useMemo(() => {
    return [4, 6, 8, 10, 12, 14, 16, 18, 20, 22];
  }, []);

  // Filter crew by search
  const filteredCrew = useMemo(() => {
    if (!searchQuery) return crew;
    const query = searchQuery.toLowerCase();
    return crew.filter(c => 
      c.name?.toLowerCase().includes(query) ||
      c.employee_id?.toLowerCase().includes(query) ||
      c.role?.toLowerCase().includes(query)
    );
  }, [crew, searchQuery]);

  // Group entries by date and hour
  const entriesBySlot = useMemo(() => {
    const grouped = {};
    for (const entry of entries) {
      const date = entry.duty_date;
      // Extract hour from report_time or use a default
      const reportTime = entry.report_time || '08:00';
      const hour = parseInt(reportTime.split(':')[0], 10) || 8;
      const nearestSlot = timeSlots.reduce((prev, curr) => 
        Math.abs(curr - hour) < Math.abs(prev - hour) ? curr : prev
      );
      const key = `${date}-${nearestSlot}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(entry);
    }
    return grouped;
  }, [entries, timeSlots]);

  // Get assignments for a specific slot
  const getSlotAssignments = useCallback((date, hour) => {
    return entriesBySlot[`${date}-${hour}`] || [];
  }, [entriesBySlot]);

  // Get conflicts for a slot
  const getSlotConflicts = useCallback((date, hour) => {
    return conflicts.filter(c => {
      if (c.date === date) return true;
      // Check if any entry in this slot has conflicts
      const slotEntries = getSlotAssignments(date, hour);
      return slotEntries.some(e => e.has_conflicts);
    });
  }, [conflicts, getSlotAssignments]);

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    if (active.data.current?.type === 'crew') {
      setActiveItem(active.data.current.crew);
    }
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveItem(null);

    if (!over || !active.data.current?.crew) return;

    const crewMember = active.data.current.crew;
    const slotData = over.data.current;

    if (slotData?.type === 'slot') {
      // Assign crew to slot
      onAssign({
        crew_member_id: crewMember.id,
        crew_member_name: crewMember.name,
        duty_date: slotData.date,
        report_time: `${slotData.hour.toString().padStart(2, '0')}:00`,
        duty_type: 'flight_duty',
      });
      toast.success(`Assigned ${crewMember.name} to ${slotData.date} at ${slotData.hour}:00`);
    }
  };

  // Get heatmap data for a date
  const getHeatmapForDate = useCallback((date) => {
    return heatmap.find(h => h.date === date) || null;
  }, [heatmap]);

  // Day header with risk indicator
  const DayHeader = ({ date }) => {
    const dayData = getHeatmapForDate(date);
    const dayDate = new Date(date + 'T00:00:00');
    const isToday = new Date().toDateString() === dayDate.toDateString();
    const dayConflicts = conflicts.filter(c => c.date === date);
    
    return (
      <div className={cn(
        "text-center p-2 rounded-t-lg border-b",
        isToday ? "bg-indigo-100 dark:bg-indigo-900/30" : "bg-slate-50 dark:bg-slate-800/50"
      )}>
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {dayDate.toLocaleDateString('en-US', { weekday: 'short' })}
        </p>
        <p className={cn(
          "text-lg font-bold",
          isToday ? "text-indigo-600 dark:text-indigo-400" : "text-slate-900 dark:text-white"
        )}>
          {dayDate.getDate()}
        </p>
        <div className="flex items-center justify-center gap-1 mt-1">
          {dayData && (
            <Badge className={cn(
              "text-xs",
              riskConfig[dayData.max_risk_level]?.bgLight,
              riskConfig[dayData.max_risk_level]?.text
            )}>
              {Math.round(dayData.average_fatigue_score)}
            </Badge>
          )}
          {dayConflicts.length > 0 && (
            <Badge className="text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              {dayConflicts.length}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  if (!roster) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Calendar className="h-12 w-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-500">Select a roster to start scheduling</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4" data-testid="drag-drop-scheduler">
        {/* Crew Panel */}
        <div className="w-72 flex-shrink-0">
          <Card className="sticky top-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-indigo-500" />
                Available Crew
              </CardTitle>
              <CardDescription>
                Drag crew members to assign duties
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search crew..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <ScrollArea className="h-[500px]">
                <div className="space-y-2 pr-3">
                  {filteredCrew.map((member) => (
                    <DraggableCrewCard
                      key={member.id}
                      crew={member}
                      fatigueScore={member.current_fatigue_score}
                      isActive={activeItem?.id === member.id}
                    />
                  ))}
                  {filteredCrew.length === 0 && (
                    <p className="text-center text-sm text-slate-400 py-8">
                      No crew members found
                    </p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Schedule Grid */}
        <div className="flex-1">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-indigo-500" />
                    Weekly Schedule
                  </CardTitle>
                  <CardDescription>
                    {roster.name} • {roster.start_date} to {roster.end_date}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConflictsOnly(!showConflictsOnly)}
                    className={showConflictsOnly ? "bg-red-50 border-red-300" : ""}
                  >
                    {showConflictsOnly ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                    Conflicts
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onAnalyze}
                    disabled={loading}
                  >
                    <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} />
                    Analyze
                  </Button>
                </div>
              </div>

              {/* Week Navigation */}
              <div className="flex items-center justify-center gap-4 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWeekOffset(w => w - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {weekDates[0] && new Date(weekDates[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' — '}
                  {weekDates[6] && new Date(weekDates[6] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWeekOffset(w => w + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <ScrollArea className="w-full" orientation="horizontal">
                <div className="min-w-[900px]">
                  {/* Day Headers */}
                  <div className="grid grid-cols-8 gap-2 mb-2">
                    <div className="w-16" /> {/* Time column spacer */}
                    {weekDates.map((date) => (
                      <DayHeader key={date} date={date} />
                    ))}
                  </div>

                  {/* Time Grid */}
                  <div className="space-y-2">
                    {timeSlots.map((hour) => (
                      <div key={hour} className="grid grid-cols-8 gap-2">
                        {/* Time Label */}
                        <div className="w-16 flex items-center justify-center text-xs font-medium text-slate-500">
                          {hour.toString().padStart(2, '0')}:00
                        </div>
                        
                        {/* Day Slots */}
                        {weekDates.map((date) => (
                          <DroppableTimeSlot
                            key={`${date}-${hour}`}
                            date={date}
                            hour={hour}
                            assignments={getSlotAssignments(date, hour)}
                            conflicts={getSlotConflicts(date, hour)}
                            onRemove={onRemove}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollArea>

              {/* Legend */}
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-xs font-medium text-slate-500 mb-2">Fatigue Risk Levels</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(riskConfig).map(([key, config]) => (
                    <div key={key} className="flex items-center gap-1.5">
                      <div className={cn("w-3 h-3 rounded-full", config.color)} />
                      <span className="text-xs text-slate-600 dark:text-slate-400">{config.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeItem && <CrewCardOverlay crew={activeItem} />}
      </DragOverlay>
    </DndContext>
  );
}
